import cx from 'classnames';
import React, { forwardRef } from 'react';
import { BaseLink } from './base-link';
import styles from './link.module.scss';
export function shouldInvokeLinkHandler(event, target) {
    return (event.button === 0 &&
        (!target || target === '_self') &&
        !(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey));
}
/**
 * `Link` should only be used in two ways:
 *
 * **1. External link — `href` + `target="_blank"`**
 *
 * Opens in a new browser tab. Always pair with `rel="noopener noreferrer"`.
 * Do not attach an `onClick` — the browser handles navigation.
 *
 * ```tsx
 * <Link href="https://example.com" target="_blank" rel="noopener noreferrer">
 *   Open in new tab
 * </Link>
 * ```
 *
 * **2. In-app (SPA) navigation — `href` + `onClick`**
 *
 * Use for internal routing. Keep `href` so that ctrl/meta/shift+click still
 * opens a new tab via native browser behaviour. In `onClick`, guard for
 * modifier keys and delegate to the app nav service.
 *
 * ```tsx
 * <Link
 *   href={url}
 *   onClick={(e) => {
 *     if (e.ctrlKey || e.metaKey || e.shiftKey) return;
 *     e.preventDefault();
 *     navService.goToPath(url);
 *   }}
 * >
 *   Go to page
 * </Link>
 * ```
 */
export const Link = forwardRef(({ children, color = 'blue', className, disabled, noUderline = false, ...restProps }, ref) => {
    const containerClassNames = cx(className, styles.link, {
        [styles.blue]: color === 'blue',
        [styles.black]: color === 'black',
        [styles.white]: color === 'white',
        [styles.disabled]: disabled,
        [styles.noUderline]: noUderline,
    });
    return (React.createElement(BaseLink, { ref: ref, className: containerClassNames, ...restProps }, children));
});
export const RoutedLink = forwardRef(({ children, allowModifiedLinkActions = true, onClick, ...restProps }, ref) => {
    const clickHandler = React.useMemo(() => {
        if (!allowModifiedLinkActions) {
            return (e) => {
                e.preventDefault();
                onClick === null || onClick === void 0 ? void 0 : onClick(e);
            };
        }
        return (e) => {
            if (shouldInvokeLinkHandler(e)) {
                e.preventDefault();
                onClick === null || onClick === void 0 ? void 0 : onClick(e);
            }
        };
    }, [allowModifiedLinkActions, onClick]);
    return (React.createElement(Link, { ref: ref, ...restProps, onClick: clickHandler }, children));
});
//# sourceMappingURL=link.js.map